import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Alert,
  alpha,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import DataObjectIcon from '@mui/icons-material/DataObject';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import TitleIcon from '@mui/icons-material/Title';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { count } from '../manage/manage';
import { CRM_EMAIL_AUDIENCE } from './email.queries';
import { INSERTABLE, previewDocument, renderMerge, SAMPLE, unknownMergeFields } from './emailModel';

/* ------------------------------------------------------------------ *
 * Inserting a merge field
 * ------------------------------------------------------------------ */

export const MergeFieldMenu = ({ onInsert, disabled, size = 'small' }: { onInsert: (tag: string) => void; disabled?: boolean; size?: 'small' | 'medium' }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Button
        size={size}
        variant="text"
        startIcon={<DataObjectIcon />}
        disabled={disabled}
        onMouseDown={(event) => event.preventDefault()}
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="menu"
      >
        Merge field
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {INSERTABLE.map((f) => (
          <MenuItem
            key={f.tag}
            onClick={() => {
              setAnchor(null);
              onInsert(f.tag);
            }}
            sx={{ display: 'block' }}
          >
            <Typography variant="body2">{f.label}</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: 'text.secondary' }}>
              {f.tag}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

/* ------------------------------------------------------------------ *
 * The body: written visually, or as HTML
 * ------------------------------------------------------------------ */

interface BodyEditorProps {
  value: string;
  onChange: (html: string) => void;
}

const exec = (command: string, arg?: string) => {
  // execCommand is deprecated but is still what every browser's editable regions run on, with no dependency to ship.
  document.execCommand(command, false, arg);
};

/**
 * The email's HTML body. "Visual" edits it as a page (bold, headings, lists,
 * links); "HTML" shows the markup, for pasted templates. Merge fields are text
 * either way, inserted where the cursor was.
 */
export const BodyEditor = ({ value, onChange }: BodyEditorProps) => {
  const [mode, setMode] = useState<'visual' | 'html'>(() => (/<(table|style|div[^>]*style)/i.test(value) ? 'html' : 'visual'));
  const editable = useRef<HTMLDivElement>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const range = useRef<Range | null>(null);

  // The editable region is not controlled by React: its HTML is set when it appears, then it reports changes.
  useEffect(() => {
    if (mode === 'visual' && editable.current && editable.current.innerHTML !== value) editable.current.innerHTML = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const remember = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && editable.current?.contains(sel.anchorNode)) range.current = sel.getRangeAt(0).cloneRange();
  };

  const restore = () => {
    editable.current?.focus();
    const sel = window.getSelection();
    if (sel && range.current) {
      sel.removeAllRanges();
      sel.addRange(range.current);
    }
  };

  const report = () => editable.current && onChange(editable.current.innerHTML);

  const command = (name: string, arg?: string) => {
    restore();
    exec(name, arg);
    report();
    remember();
  };

  const insert = (tag: string) => {
    if (mode === 'visual') {
      restore();
      exec('insertText', tag);
      report();
      remember();
      return;
    }
    const el = textarea.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    onChange(`${value.slice(0, start)}${tag}${value.slice(end)}`);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + tag.length, start + tag.length);
    });
  };

  const link = () => {
    const url = window.prompt('Link to (https://…)');
    if (url) command('createLink', url);
  };

  const tools: Array<{ label: string; icon: React.ReactNode; run: () => void }> = [
    { label: 'Bold', icon: <FormatBoldIcon />, run: () => command('bold') },
    { label: 'Italic', icon: <FormatItalicIcon />, run: () => command('italic') },
    { label: 'Underline', icon: <FormatUnderlinedIcon />, run: () => command('underline') },
    { label: 'Heading', icon: <TitleIcon />, run: () => command('formatBlock', 'h2') },
    { label: 'Paragraph', icon: <Box component="span" sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1 }}>¶</Box>, run: () => command('formatBlock', 'p') },
    { label: 'Bulleted list', icon: <FormatListBulletedIcon />, run: () => command('insertUnorderedList') },
    { label: 'Numbered list', icon: <FormatListNumberedIcon />, run: () => command('insertOrderedList') },
    { label: 'Link', icon: <InsertLinkIcon />, run: link },
    { label: 'Clear formatting', icon: <FormatClearIcon />, run: () => command('removeFormat') },
  ];

  return (
    <Box sx={{ border: '1px solid', borderColor: '#d6dce6', borderRadius: 2, overflow: 'hidden', bgcolor: '#fff', '&:focus-within': { borderColor: 'primary.main', boxShadow: '0 0 0 3px rgba(79,70,229,.14)' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, px: 0.75, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#f8fafc', flexWrap: 'wrap' }}>
        {mode === 'visual' &&
          tools.map((t) => (
            <Tooltip key={t.label} title={t.label}>
              <IconButton size="small" aria-label={t.label} onMouseDown={(event) => event.preventDefault()} onClick={t.run} sx={{ '& svg': { fontSize: 18 } }}>
                {t.icon}
              </IconButton>
            </Tooltip>
          ))}
        {mode === 'visual' && <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />}
        <MergeFieldMenu onInsert={insert} />
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, next) => {
            if (!next) return;
            if (mode === 'visual') report();
            setMode(next);
          }}
          sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1, fontSize: '0.75rem' } }}
        >
          <ToggleButton value="visual" aria-label="Write visually">
            <EditOutlinedIcon sx={{ fontSize: 15, mr: 0.5 }} />
            Visual
          </ToggleButton>
          <ToggleButton value="html" aria-label="Edit the HTML">
            <CodeIcon sx={{ fontSize: 15, mr: 0.5 }} />
            HTML
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {mode === 'visual' ? (
        <Box
          ref={editable}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline
          aria-label="Email body"
          onInput={report}
          onKeyUp={remember}
          onMouseUp={remember}
          onBlur={() => {
            remember();
            report();
          }}
          sx={{
            minHeight: 320,
            maxHeight: 640,
            overflowY: 'auto',
            p: 2,
            outline: 'none',
            fontSize: 15,
            lineHeight: 1.6,
            '&:empty::before': { content: '"Write the email. Use Merge field for each person’s name."', color: 'text.disabled' },
            '& h2': { fontSize: '1.25rem', m: '0.6em 0 0.3em' },
            '& p': { m: '0 0 0.8em' },
            '& a': { color: 'primary.main' },
          }}
        />
      ) : (
        <Box
          component="textarea"
          ref={textarea}
          aria-label="Email body HTML"
          value={value}
          spellCheck={false}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => onChange(event.target.value)}
          sx={{
            display: 'block',
            width: '100%',
            minHeight: 320,
            border: 0,
            outline: 'none',
            resize: 'vertical',
            p: 2,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 13,
            lineHeight: 1.55,
            color: '#0f172a',
            bgcolor: '#fff',
          }}
        />
      )}
    </Box>
  );
};

/* ------------------------------------------------------------------ *
 * The preview: the inbox line, then the email as a person gets it
 * ------------------------------------------------------------------ */

interface EmailPreviewProps {
  fromName: string;
  subject: string;
  preheader: string;
  bodyHtml: string;
}

export const EmailPreview = ({ fromName, subject, preheader, bodyHtml }: EmailPreviewProps) => {
  const [device, setDevice] = useState<'desktop' | 'phone'>('desktop');
  const doc = useMemo(() => previewDocument(bodyHtml), [bodyHtml]);
  const bad = unknownMergeFields(subject, preheader, bodyHtml);

  return (
    <Box sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden', bgcolor: '#eef1f5' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="overline" color="text.secondary" sx={{ flex: 1 }}>
          Preview, as {SAMPLE.firstName} {SAMPLE.lastName}
        </Typography>
        <ToggleButtonGroup size="small" exclusive value={device} onChange={(_, d) => d && setDevice(d)}>
          <ToggleButton value="desktop" aria-label="Desktop width">
            <DesktopWindowsOutlinedIcon sx={{ fontSize: 16 }} />
          </ToggleButton>
          <ToggleButton value="phone" aria-label="Phone width">
            <PhoneIphoneIcon sx={{ fontSize: 16 }} />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ p: { xs: 1, sm: 2 }, display: 'flex', justifyContent: 'center' }}>
        <Box
          sx={{
            width: device === 'phone' ? 375 : '100%',
            maxWidth: '100%',
            bgcolor: '#fff',
            borderRadius: device === 'phone' ? '28px' : 2,
            border: device === 'phone' ? '10px solid #1e293b' : '1px solid #e2e8f0',
            overflow: 'hidden',
            boxShadow: '0 10px 30px rgba(15,23,42,.10)',
            transition: 'width 220ms ease',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', p: 1.5, borderBottom: '1px solid #eef1f5' }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: alpha('#4f46e5', 0.12), color: 'primary.main', display: 'grid', placeItems: 'center', fontWeight: 700, flexShrink: 0 }}>
              {(fromName || 'I').trim().charAt(0).toUpperCase()}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 650, fontSize: '0.85rem' }} noWrap>
                {fromName || 'The default sender'}
              </Typography>
              <Typography sx={{ fontSize: '0.85rem' }} noWrap>
                {subject ? renderMerge(subject, SAMPLE, false) : <Box component="span" sx={{ color: 'text.disabled' }}>No subject yet</Box>}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {preheader ? renderMerge(preheader, SAMPLE, false) : 'The preheader shows here in most inboxes'}
              </Typography>
            </Box>
          </Box>
          <Box component="iframe" title="Email preview" sandbox="allow-popups" srcDoc={doc} sx={{ display: 'block', width: '100%', height: device === 'phone' ? 560 : 520, border: 0 }} />
        </Box>
      </Box>
      {bad.length > 0 && (
        <Alert severity="warning" sx={{ m: 1.5, mt: 0 }}>
          These cannot be filled and would stop it sending: {bad.join(', ')}. NationBuilder’s {'{% if %}'} logic is not supported.
        </Alert>
      )}
    </Box>
  );
};

/* ------------------------------------------------------------------ *
 * Who it reaches
 * ------------------------------------------------------------------ */

export interface AudienceCounts {
  total: number;
  withEmail: number;
  emailOptIn: number;
  unsubscribed: number;
  emailIsBad: number;
  doNotContact: number;
  canEmail: number;
}

/**
 * The consent breakdown of a list: how many are on it and how many can be
 * emailed. `canEmail` is the only number it sends to, and the one confirmed
 * when scheduling.
 */
export const AudienceBreakdown = ({ segmentId, onCounts }: { segmentId: number; onCounts?: (counts: AudienceCounts | null) => void }) => {
  const { data, loading, error } = useQuery<{ crmEmailAudience: AudienceCounts }>(CRM_EMAIL_AUDIENCE, { variables: { segmentId }, fetchPolicy: 'cache-and-network' });
  const a = data?.crmEmailAudience;
  useEffect(() => onCounts?.(a ?? null), [a, onCounts]);

  if (error) return <Alert severity="error">Could not count who it reaches.</Alert>;
  if (!a) return loading ? <Skeleton variant="rounded" height={96} /> : null;

  const share = (n: number) => (a.total > 0 ? `${Math.max(0, Math.min(100, (n / a.total) * 100))}%` : '0%');
  const rows = [
    { label: 'No email address', n: a.total - a.withEmail },
    { label: 'Not opted in or unsubscribed', n: Math.max(0, a.withEmail - a.emailOptIn) },
    { label: 'Address marked bad', n: a.emailIsBad },
    { label: 'Do not contact', n: a.doNotContact },
  ].filter((r) => r.n > 0);

  return (
    <Box sx={{ display: 'grid', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography sx={{ fontSize: '1.6rem', fontWeight: 750, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{count(a.canEmail)}</Typography>
        <Typography variant="body2">can be emailed</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', fontVariantNumeric: 'tabular-nums' }}>
          of {count(a.total)} on it
        </Typography>
      </Box>
      <Box aria-hidden sx={{ height: 8, borderRadius: 4, bgcolor: '#eef1f5', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: share(a.canEmail), background: 'linear-gradient(90deg, #4f46e5, #7c3aed)', transition: 'width 400ms' }} />
      </Box>
      {rows.length > 0 && (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gap: 0.25 }}>
          {rows.map((r) => (
            <Box component="li" key={r.label} sx={{ display: 'flex', fontSize: '0.8rem', color: 'text.secondary' }}>
              <Box sx={{ flex: 1 }}>{r.label}</Box>
              <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>{count(r.n)}</Box>
            </Box>
          ))}
        </Box>
      )}
      <Typography variant="caption" color="text.secondary">
        Only people opted in, with a good address and not do-not-contact are ever sent to. Each is checked again as their email goes.
      </Typography>
    </Box>
  );
};
